import React, { useEffect, useState } from "react";
import BreadCrumb from "../components/BreadCrumb";
import Meta from "../components/Meta";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Container from "../components/Container";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { registerUser, resetState } from "../features/user/userSlice";
import apiClient from "../utils/axiosConfig"; // Import apiClient
import { trackCompleteRegistration } from "../utils/metaPixel";
import { 
  BsEye, 
  BsEyeSlash, 
  BsPerson, 
  BsEnvelope, 
  BsLock, 
  BsTelephone, 
  BsArrowRight, 
  BsShieldCheck, 
  BsCheckCircle 
} from "react-icons/bs";

// Enhanced validation schema with better security and error handling
let signUpSchema = yup.object({
  firstname: yup
    .string()
    .required("First Name is Required")
    .min(2, "First Name must be at least 2 characters")
    .max(50, "First Name cannot exceed 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "First Name can only contain letters and spaces"),
  lastname: yup
    .string()
    .required("Last Name is Required")
    .min(2, "Last Name must be at least 2 characters")
    .max(50, "Last Name cannot exceed 50 characters")
    .matches(/^[a-zA-Z\s]+$/, "Last Name can only contain letters and spaces"),
  email: yup
    .string()
    .required("Email is Required")
    .email("Email Should be valid")
    .max(100, "Email cannot exceed 100 characters")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email address"
    ),
  mobile: yup
    .string()
    .required("Mobile No is Required")
    .matches(/^[0-9]{10}$/, "Mobile number must be exactly 10 digits")
    .test("is-valid-mobile", "Invalid mobile number", (value) => {
      if (!value) return false;
      // Additional validation to prevent common invalid patterns
      const invalidPatterns = [
        /^0+$/, // All zeros
        /^1+$/, // All ones
        /^(\d)\1{9}$/, // All same digits
      ];
      return !invalidPatterns.some(pattern => pattern.test(value));
    }),
  password: yup
    .string()
    .required("Password is Required")
    .min(8, "Password must be at least 8 characters")
    .max(128, "Password cannot exceed 128 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number and one special character"
    ),
});

const Signup = () => {
  const authState = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  // Check if user came from guest order
  const fromGuestOrder = location.state?.fromGuestOrder;
  const guestOrderId = location.state?.orderId;

  // Initialize formik after all hooks
  const formik = useFormik({
    initialValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      password: "",
      subscribeToNewsletter: true,
    },
    validationSchema: signUpSchema,
    onSubmit: (values) => {
      setIsLoading(true);
      setErrorMessage("");
      // Register user first
      dispatch(registerUser(values));
    },
  });

  useEffect(() => {
    dispatch(resetState());
    if (guestOrderId) {
      setOrderId(guestOrderId);
    }
  }, [dispatch, guestOrderId]);

  useEffect(() => {
    if (authState?.isSuccess && authState?.createdUser) {
      setIsLoading(false);
      setErrorMessage("");
      
      // Track CompleteRegistration event
      trackCompleteRegistration('email');
      
      // Subscribe to newsletter if opted in
      if (formik.values.subscribeToNewsletter) {
        subscribeToMailchimpNewsletter();
      }
      
      if (fromGuestOrder) {
        setShowSuccess(true);
        // Clear guest user ID from localStorage after successful registration
        localStorage.removeItem("guestUserId");
        // Auto redirect to orders page after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        // Clear guest user ID from localStorage after successful registration
        localStorage.removeItem("guestUserId");
        // Show success message about email verification
        setShowSuccess(true);
        // Auto redirect to login page after 5 seconds
        setTimeout(() => {
          navigate("/login");
        }, 5000);
      }
    } else if (authState?.isError) {
      setIsLoading(false);
      // Handle specific error messages from backend
      const errorMsg = authState?.message?.response?.data?.message || 
                      authState?.message?.message || 
                      "An error occurred during registration";
      
      // Provide user-friendly error messages
      if (errorMsg.includes("email already exists")) {
        setErrorMessage("An account with this email already exists. Please use a different email.");
      } else if (errorMsg.includes("mobile number already exists")) {
        setErrorMessage("An account with this mobile number already exists. Please use a different mobile number.");
      } else if (errorMsg.includes("User Already Exists")) {
        setErrorMessage("An account with this email or mobile number already exists.");
      } else {
        setErrorMessage(errorMsg);
      }
    }
  }, [authState, navigate, fromGuestOrder, formik.values.subscribeToNewsletter]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Function to subscribe user to Mailchimp newsletter
  const subscribeToMailchimpNewsletter = async () => {
    try {
      await apiClient.post('/newsletter/subscribe', {
        email: formik.values.email,
        firstName: formik.values.firstname,
        lastName: formik.values.lastname
      });
    } catch (error) {
      console.error("Failed to subscribe to newsletter:", error);
      // Don't fail the registration if newsletter subscription fails
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-yellow-50 to-red-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full mb-4 shadow-lg">
              <BsPerson className="text-white text-3xl" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2 fredoka">Welcome to VEDA</h2>
            <p className="text-gray-600">Join us and start your shopping journey</p>
          </div>

          {/* Success Message */}
          {showSuccess && (
            <div className="mb-6 bg-green-50 border-2 border-green-200 rounded-2xl p-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <BsCheckCircle className="text-green-500 text-3xl" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 fredoka">Account Created Successfully!</h3>
              {fromGuestOrder ? (
                <>
                  <p className="text-gray-600 mb-4">
                    Your guest order {orderId ? (authState?.orderedProduct?.order?.customOrderId || `#${orderId.slice(-8).toUpperCase()}`) : ''} has been transferred to your new account.
                  </p>
                  <p className="text-gray-600">
                    Redirecting to your orders page...
                  </p>
                </>
              ) : (
                <>
                  <p className="text-gray-600 mb-4">
                    Please check your email to verify your account. You will be redirected to the login page shortly.
                  </p>
                  <p className="text-gray-600">
                    Redirecting to login page...
                  </p>
                </>
              )}
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl p-8">
            {/* Error Alert */}
            {(authState?.isError || errorMessage) && (
              <div className="mb-6 bg-red-50 border-2 border-red-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800">
                      {errorMessage || authState?.message?.response?.data?.message || "An error occurred during registration"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Guest Order Notice */}
            {fromGuestOrder && !showSuccess && (
              <div className="mb-6 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <BsCheckCircle className="text-yellow-500 text-xl" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-semibold text-yellow-800 fredoka">Guest Order Detected</h3>
                    <p className="text-sm text-yellow-700">
                      Your order {orderId ? (authState?.orderedProduct?.order?.customOrderId || `#${orderId.slice(-8).toUpperCase()}`) : ''} will be automatically transferred to your new account after registration.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={formik.handleSubmit} className="space-y-6">
              {/* First Name & Last Name Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstname" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                    First Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsPerson className="text-red-500 text-lg" />
                    </div>
                    <input
                      type="text"
                      name="firstname"
                      id="firstname"
                      placeholder="Enter your first name"
                      value={formik.values.firstname}
                      onChange={formik.handleChange("firstname")}
                      onBlur={formik.handleBlur("firstname")}
                      className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        formik.touched.firstname && formik.errors.firstname
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                  </div>
                  {formik.touched.firstname && formik.errors.firstname && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.firstname}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastname" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                    Last Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <BsPerson className="text-red-500 text-lg" />
                    </div>
                    <input
                      type="text"
                      name="lastname"
                      id="lastname"
                      placeholder="Enter your last name"
                      value={formik.values.lastname}
                      onChange={formik.handleChange("lastname")}
                      onBlur={formik.handleBlur("lastname")}
                      className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                        formik.touched.lastname && formik.errors.lastname
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    />
                  </div>
                  {formik.touched.lastname && formik.errors.lastname && (
                    <p className="mt-2 text-sm text-red-600">{formik.errors.lastname}</p>
                  )}
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BsEnvelope className="text-red-500 text-lg" />
                  </div>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    placeholder="Enter your email address"
                    value={formik.values.email}
                    onChange={formik.handleChange("email")}
                    onBlur={formik.handleBlur("email")}
                    className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      formik.touched.email && formik.errors.email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                </div>
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.email}</p>
                )}
              </div>

              {/* Mobile Field */}
              <div>
                <label htmlFor="mobile" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                  Mobile Number
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BsTelephone className="text-red-500 text-lg" />
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    id="mobile"
                    placeholder="Enter your mobile number"
                    value={formik.values.mobile}
                    onChange={formik.handleChange("mobile")}
                    onBlur={formik.handleBlur("mobile")}
                    className={`block w-full pl-12 pr-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      formik.touched.mobile && formik.errors.mobile
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                </div>
                {formik.touched.mobile && formik.errors.mobile && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.mobile}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2 fredoka">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <BsLock className="text-red-500 text-lg" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    id="password"
                    placeholder="Create a strong password"
                    value={formik.values.password}
                    onChange={formik.handleChange("password")}
                    onBlur={formik.handleBlur("password")}
                    className={`block w-full pl-12 pr-12 py-3 border-2 rounded-2xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all ${
                      formik.touched.password && formik.errors.password
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    {showPassword ? <BsEyeSlash className="text-xl" /> : <BsEye className="text-xl" />}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <p className="mt-2 text-sm text-red-600">{formik.errors.password}</p>
                )}
                <p className="mt-2 text-xs text-gray-500">
                  Password must contain at least 8 characters, including uppercase, lowercase, number and special character.
                </p>
              </div>

              {/* Newsletter Subscription */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="subscribeToNewsletter"
                  checked={formik.values.subscribeToNewsletter}
                  onChange={() => formik.setFieldValue("subscribeToNewsletter", !formik.values.subscribeToNewsletter)}
                  className="h-5 w-5 mt-1 text-red-500 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="subscribeToNewsletter" className="ml-2 block text-sm text-gray-700 cursor-pointer fredoka">
                  Subscribe to our newsletter for updates on new products, special offers, and mythological stories
                </label>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="terms"
                  required
                  className="h-5 w-5 mt-1 text-red-500 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                />
                <label htmlFor="terms" className="ml-2 block text-sm text-gray-700 cursor-pointer fredoka">
                  I agree to the{" "}
                  <Link to="/terms-and-conditions-policy" className="font-semibold text-red-500 hover:text-red-600 transition-colors">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy-policy" className="font-semibold text-red-500 hover:text-red-600 transition-colors">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading || showSuccess}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white font-bold rounded-full shadow-lg hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Creating Account...</span>
                  </>
                ) : showSuccess ? (
                  <>
                    <BsCheckCircle className="text-xl" />
                    <span>Redirecting...</span>
                  </>
                ) : (
                  <>
                    <span>Create Account</span>
                    <BsArrowRight className="text-xl" />
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">or</span>
                </div>
              </div>

              {/* Login Link */}
              <div className="text-center">
                <p className="text-gray-600">
                  Already have an account?{" "}
                  <Link 
                    to="/login" 
                    state={{ fromGuestOrder, orderId: guestOrderId }}
                    className="font-bold text-red-500 hover:text-red-600 transition-colors"
                  >
                    Sign in here
                  </Link>
                </p>
              </div>
            </form>
          </div>

          {/* Trust Badges */}
          <div className="grid grid-cols-3 gap-4 mt-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsShieldCheck className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Secure Signup</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-100 rounded-full mb-2">
                <BsLock className="text-yellow-600 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Data Protected</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-100 rounded-full mb-2">
                <BsEnvelope className="text-red-500 text-2xl" />
              </div>
              <p className="text-xs text-gray-600 font-medium">Email Verified</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;